import Text from "common/Typography/Text";
import React, { CSSProperties } from "react";
import styled, { useTheme } from "styled-components";
import { typography } from "@shared/common/Themes/typography";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_VIRTUAL_LIST } from "common/constants";

interface PublicBotStatisticProps {
  statisticKey: string;
  statisticValue: string;
  showBorder?: boolean;
  style?: CSSProperties;
  isNegative?: boolean;
}

const PublicBotStatistic = ({
  statisticKey,
  statisticValue,
  showBorder = true,
  style,
  isNegative,
}: PublicBotStatisticProps) => {
  const isMobile = useMedia(
    [`(max-width: ${MOBILE_VIRTUAL_LIST})`],
    [true],
    false
  );

  const { isNewLook } = useTheme();

  if (isNewLook) {
    return (
      <NewLookCell style={style} $compact={!showBorder}>
        <NewLookLabel>{statisticKey}</NewLookLabel>
        <NewLookValue $isNegative={isNegative} $compact={!showBorder}>
          {statisticValue}
        </NewLookValue>
      </NewLookCell>
    );
  }

  return (
    <Wrapper $showBorder={showBorder} style={style}>
      <Text
        type={isNegative ? "danger" : "success"}
        $size={isMobile ? typography.fontSizes.M : typography.fontSizes.XL}
        $weight={typography.fontWeights.SEMI_BOLD}
      >
        {statisticValue}
      </Text>
      <Text
        type="secondary"
        $size={typography.fontSizes.S}
        style={{ whiteSpace: "wrap", textAlign: "center" }}
      >
        {statisticKey}
      </Text>
    </Wrapper>
  );
};

export default PublicBotStatistic;

const Wrapper = styled.div<{ $showBorder: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  flex-direction: column;
  ${(p) =>
    p.$showBorder &&
    `
  &:not(:last-of-type) {
    border-right: ${p.theme.borders.light};
    padding: 0 8px;
  }
  `};
`;

// Labels wrap instead of truncating in narrow cards (phone, ~330px); the
// values sit on one baseline at the bottom of the cells either way.
const NewLookCell = styled.div<{ $compact?: boolean }>`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  padding: ${(p) => (p.$compact ? "10px" : "16px")};
  @media (max-width: 450px) {
    padding: ${(p) => (p.$compact ? "10px 8px" : "12px")};
  }
`;
const NewLookLabel = styled.div`
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 12px;
  line-height: 16px;
  overflow-wrap: break-word;
`;
const NewLookValue = styled.div<{ $isNegative?: boolean; $compact?: boolean }>`
  margin-top: auto;
  padding-top: ${(p) => (p.$compact ? 4 : 6)}px;
  color: ${(p) => (p.$isNegative ? p.theme.colors.red : p.theme.colors.green)};
  font-size: ${(p) => (p.$compact ? 15 : 18)}px;
  line-height: ${(p) => (p.$compact ? 22 : 24)}px;
  /* 18px figures 600; the compact 15px values 500. */
  font-weight: ${(p) =>
    p.$compact
      ? p.theme.typography.fontWeights.MEDIUM
      : p.theme.typography.fontWeights.SEMI_BOLD};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;
